<script>
    import { onMount } from 'svelte';
    import { querystring } from 'svelte-spa-router';
    import { to } from "@API";
    import { Language } from "@Store";

    let password = '';
    let confirmPassword = '';
    let submitting = false;
    let error = null;
    let success = false;
    let uid = '';
    let token = '';

    onMount(() => {
        // Parse query parameters from the URL
        const params = new URLSearchParams($querystring);
        uid = params.get('uid') || '';
        token = params.get('token') || '';

        if (!uid || !token) {
            error = { message: $Language === 'en'
                ? 'Invalid password reset link. Please request a new one.'
                : 'Lien de réinitialisation invalide. Veuillez en demander un nouveau.' };
        }
    });

    async function handleResetPassword() {
        error = null;

        if (!password || password.length < 8) {
            error = { message: $Language === 'en'
                ? 'Password must be at least 8 characters'
                : 'Le mot de passe doit contenir au moins 8 caractères' };
            return;
        }

        if (password !== confirmPassword) {
            error = { message: $Language === 'en'
                ? 'Passwords do not match'
                : 'Les mots de passe ne correspondent pas' };
            return;
        }

        submitting = true;

        let [err, response] = await to(
            Insurecert.API.registration().resetPassword({
                uid: uid,
                token: token,
                password: password
            })
        );

        submitting = false;

        if (err) {
            error = err.data || { message: $Language === 'en'
                ? 'Failed to reset password. The link may have expired.'
                : 'Échec de la réinitialisation du mot de passe. Le lien a peut-être expiré.' };
            return;
        }

        if (response) {
            success = true;
        }
    }

    function getErrorMessage(errorData) {
        if (typeof errorData === 'string') {
            return errorData;
        }
        if (errorData.message) {
            return errorData.message;
        }
        if (errorData.non_field_errors) {
            return Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors;
        }
        if (errorData.password) {
            return Array.isArray(errorData.password) ? errorData.password[0] : errorData.password;
        }
        if (errorData.token) {
            return $Language === 'en'
                ? 'Invalid or expired reset link. Please request a new one.'
                : 'Lien de réinitialisation invalide ou expiré. Veuillez en demander un nouveau.';
        }
        if (errorData.uid) {
            return $Language === 'en'
                ? 'Invalid reset link. Please request a new one.'
                : 'Lien de réinitialisation invalide. Veuillez en demander un nouveau.';
        }
        return $Language === 'en'
            ? 'An error occurred. Please try again.'
            : 'Une erreur est survenue. Veuillez réessayer.';
    }
</script>

<div class="reset-container">
    <div class="reset-card">
        <div class="reset-header">
            <div class="brand-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="20" stroke="#0071e3" stroke-width="2"/>
                    <path d="M24 14v10M24 28h.01" stroke="#0071e3" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
            </div>

            {#if success}
                <h1 class="reset-title">{$Language === 'en' ? 'Password Reset Complete' : 'Réinitialisation terminée'}</h1>
                <p class="reset-subtitle">{$Language === 'en' ? 'Your password has been successfully changed' : 'Votre mot de passe a été modifié avec succès'}</p>
            {:else}
                <h1 class="reset-title">{$Language === 'en' ? 'Set New Password' : 'Définir un nouveau mot de passe'}</h1>
                <p class="reset-subtitle">{$Language === 'en' ? 'Enter your new password below' : 'Entrez votre nouveau mot de passe ci-dessous'}</p>
            {/if}
        </div>

        <div class="reset-content">
            {#if success}
                <div class="success-message">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                        <path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <div>
                        <p>{$Language === 'en' ? 'Your password has been reset successfully.' : 'Votre mot de passe a été réinitialisé avec succès.'}</p>
                        <p>{$Language === 'en' ? 'You can now log in with your new password.' : 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.'}</p>
                    </div>
                </div>
                <a href="/#/" class="reset-button primary">
                    {$Language === 'en' ? 'Go to Login' : 'Aller à la connexion'}
                </a>
            {:else}
                <form on:submit|preventDefault={handleResetPassword} class="reset-form">
                    <div class="input-group">
                        <label for="password" class="input-label">{$Language === 'en' ? 'New Password' : 'Nouveau mot de passe'}</label>
                        <input
                            type="password"
                            id="password"
                            bind:value={password}
                            placeholder="{$Language === 'en' ? 'Enter new password' : 'Entrez le nouveau mot de passe'}"
                            class="reset-input"
                            required
                            disabled={!uid || !token}
                        />
                        <div class="input-hint">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <circle cx="6" cy="6" r="5" stroke="#86868b" stroke-width="1"/>
                                <path d="M6 3v3M6 7h.01" stroke="#86868b" stroke-width="1" stroke-linecap="round"/>
                            </svg>
                            {$Language === 'en' ? 'Use 8 or more characters' : 'Utilisez 8 caractères ou plus'}
                        </div>
                    </div>

                    <div class="input-group">
                        <label for="confirm-password" class="input-label">{$Language === 'en' ? 'Confirm New Password' : 'Confirmer le nouveau mot de passe'}</label>
                        <input
                            type="password"
                            id="confirm-password"
                            bind:value={confirmPassword}
                            placeholder="{$Language === 'en' ? 'Re-enter new password' : 'Réentrez le nouveau mot de passe'}"
                            class="reset-input"
                            required
                            disabled={!uid || !token}
                        />
                    </div>

                    {#if error}
                        <div class="error-message">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
                                <path d="M8 4v4M8 10h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                            {getErrorMessage(error)}
                        </div>
                    {/if}

                    <button type="submit" class="reset-button primary" disabled={submitting || !uid || !token}>
                        {#if submitting}
                            <svg class="spinner" width="16" height="16" viewBox="0 0 16 16">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="37.7" stroke-dashoffset="37.7">
                                    <animate attributeName="stroke-dasharray" dur="1.5s" values="0 37.7;18.85 18.85;0 37.7" repeatCount="indefinite"/>
                                    <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-18.85;-37.7" repeatCount="indefinite"/>
                                </circle>
                            </svg>
                            {$Language === 'en' ? 'Resetting Password...' : 'Réinitialisation en cours...'}
                        {:else}
                            {$Language === 'en' ? 'Reset Password' : 'Réinitialiser le mot de passe'}
                        {/if}
                    </button>

                    <a href="/#/" class="reset-button secondary">
                        {$Language === 'en' ? 'Back to Home' : 'Retour à l\'accueil'}
                    </a>
                </form>
            {/if}
        </div>
    </div>
</div>

<style>
    .reset-container {
        min-height: calc(100vh - 100px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
    }

    .reset-card {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        border-radius: 24px;
        max-width: 420px;
        width: 100%;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }

    .reset-header {
        text-align: center;
        padding: 2.5rem 2rem 1rem;
        border-bottom: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    .brand-icon {
        margin-bottom: 1.5rem;
    }

    .reset-title {
        font-size: 2rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0 0 0.5rem 0;
        letter-spacing: -0.02em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .reset-subtitle {
        font-size: 1rem;
        color: #86868b;
        margin: 0;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .reset-content {
        padding: 2rem;
    }

    .reset-form {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .input-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .input-label {
        font-size: 0.9375rem;
        font-weight: 500;
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .reset-input {
        padding: 1rem 1.25rem;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 12px;
        font-size: 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(10px);
        transition: all 0.2s ease;
        outline: none;
    }

    .reset-input:focus {
        border-color: #0071e3;
        background: rgba(255, 255, 255, 0.95);
        box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
    }

    .reset-input:disabled {
        background: rgba(245, 245, 247, 0.8);
        cursor: not-allowed;
    }

    .reset-input::placeholder {
        color: #86868b;
    }

    .input-hint {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.8125rem;
        color: #86868b;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .reset-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        font-size: 1rem;
        font-weight: 600;
        border: none;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        outline: none;
        text-decoration: none;
    }

    .reset-button.primary {
        background: #0071e3;
        color: white;
    }

    .reset-button.primary:hover:not(:disabled) {
        background: #0077ed;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 113, 227, 0.3);
    }

    .reset-button.primary:disabled {
        background: #d2d2d7;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    .reset-button.secondary {
        background: transparent;
        color: #0071e3;
        font-weight: 500;
    }

    .reset-button.secondary:hover {
        background: rgba(0, 113, 227, 0.1);
    }

    .spinner {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .error-message {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 1.25rem;
        background: rgba(255, 235, 238, 0.8);
        border: 1px solid rgba(198, 40, 40, 0.2);
        border-radius: 12px;
        color: #c62828;
        font-size: 0.9375rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        animation: shake 0.5s ease;
    }

    .success-message {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        padding: 1.25rem;
        background: rgba(232, 245, 233, 0.9);
        border: 1px solid rgba(46, 125, 50, 0.2);
        border-radius: 12px;
        color: #2e7d32;
        font-size: 0.9375rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        margin-bottom: 1.5rem;
        line-height: 1.5;
    }

    .success-message svg {
        flex-shrink: 0;
        margin-top: 0.125rem;
    }

    .success-message p {
        margin: 0 0 0.5rem 0;
    }

    .success-message p:last-child {
        margin-bottom: 0;
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
    }

    @media (max-width: 480px) {
        .reset-container {
            padding: 1rem;
        }

        .reset-card {
            border-radius: 20px;
        }

        .reset-header {
            padding: 2rem 1.5rem 1rem;
        }

        .reset-title {
            font-size: 1.75rem;
        }

        .reset-content {
            padding: 1.5rem;
        }

        .reset-form {
            gap: 1.25rem;
        }
    }
</style>
